import type { AgGridReact } from 'ag-grid-react';
import { useAtom } from 'jotai';
import { type RefObject, useCallback, useState } from 'react';

import { selectedCloudSourceRowsAtom } from '@/pages/Administration/Devices/Devices.store';
import useCustomNotification, {
  type CustomNotificationProps
} from '@/pages/Administration/Devices/SyncDevices/useCustomNotification';

import { useSourceDialog } from '../../SourceCreate/SourceCreate.hooks';
import type { ToggleActiveStatePayload } from '../../SourceCreate/SourceCreate.types';
import type { CloudSourceTreeNode } from '../CloudSourcesTable.types';
import type { SingleSourceToggleRequest } from './useCloudSourcesActions.types';

/**
 * Custom hook for managing bulk activation/deactivation of cloud sources.
 *
 * Business Logic:
 * - Determines action label based on selection state:
 *   - All active sources → "Deactivate"
 *   - All inactive sources → "Activate"
 *   - Mixed states → "Update Activation"
 * - Shows confirmation dialog before performing bulk operations
 * - Handles success/error notifications via custom notification system
 * - Clears grid selection after successful mutation
 *
 * @returns Object containing:
 * - `selectedActivationCount` - Number of sources currently selected
 * - `actionLabel` - Label for the bulk action button ("Activate", "Deactivate", or "Update Activation")
 * - `handleActivateDeactivate` - Handler to initiate bulk activation/deactivation with confirmation
 * - `handleCancelSelection` - Handler to clear grid selection
 * - `ConfirmActivateDeactivateModal` - Modal component for confirmation dialog
 *
 * @example
 * ```tsx
 * const activation = useCloudSourceActivation();
 * <Button onClick={activation.handleActivateDeactivate}>
 *   {activation.actionLabel}
 * </Button>
 * <activation.ConfirmActivateDeactivateModal />
 * ```
 */
export const useCloudSourceActivation = () => {
  const [selectedCloudSourceRows, setSelectedCloudSourceRows] = useAtom(
    selectedCloudSourceRowsAtom
  );
  const selectedActivationCount = selectedCloudSourceRows?.length || 0;

  const [customNotifyProps, setCustomNotifyProps] =
    useState<CustomNotificationProps>({
      title: '',
      message: '',
      severity: 'info',
      buttonType: 'Close'
    });

  const {
    ConfirmationModal: ConfirmActivateDeactivateModal,
    confirm: confirmActivateDeactivate
  } = useCustomNotification(customNotifyProps);

  const { updateActivationToggleMutation } = useSourceDialog();

  const getActionForSelectedRows = useCallback(() => {
    if (selectedCloudSourceRows.length === 0) return null;

    const allActive = selectedCloudSourceRows.every((row) => row.active);
    const allInactive = selectedCloudSourceRows.every((row) => !row.active);

    if (allActive) return 'deactivate';
    if (allInactive) return 'activate';

    return 'mixed'; // some active, some inactive
  }, [selectedCloudSourceRows]);

  const getActionLabel = useCallback(() => {
    const action = getActionForSelectedRows();
    let label = '';

    if (action === 'activate') {
      label = 'Activate';
    } else if (action === 'deactivate') {
      label = 'Deactivate';
    } else if (action === 'mixed') {
      label = 'Update Activation';
    }
    return label;
  }, [getActionForSelectedRows]);

  const confirmAndToggleActivation = useCallback(
    async ({
      source_uids,
      activate,
      confirmation_msg,
      onSuccess
    }: {
      source_uids: string[];
      activate: boolean;
      confirmation_msg: string;
      onSuccess?: () => void;
    }) => {
      const actionLabel = activate ? 'Activate' : 'Deactivate';

      setCustomNotifyProps({
        title: `${actionLabel}`,
        message: confirmation_msg,
        severity: 'warning',
        buttonType: 'ConfirmCancel'
      });

      const confirmed = await confirmActivateDeactivate();
      if (!confirmed) return;

      updateActivationToggleMutation.mutate(
        {
          source_uids,
          active: activate
        } as ToggleActiveStatePayload,
        {
          onSuccess: () => {
            onSuccess?.();
          }
        }
      );
    },
    [
      confirmActivateDeactivate,
      updateActivationToggleMutation,
      setCustomNotifyProps
    ]
  );

  const handleActivateDeactivate = useCallback(
    async (
      gridRef?: RefObject<AgGridReact<CloudSourceTreeNode> | null>,
      onSuccess?: () => void
    ) => {
      if (!selectedCloudSourceRows?.length) return;

      const source_uids = selectedCloudSourceRows
        .map((row) => row?.sourceData?.source_uid)
        .filter((uid): uid is string => uid !== undefined);

      const action = getActionForSelectedRows();
      const activate = action === 'activate' || action === 'mixed';

      let confirmation_msg = '';
      if (action === 'mixed') {
        confirmation_msg = `Activate the selected inactive sources?`;
      } else {
        confirmation_msg = `${activate ? 'Activate' : 'Deactivate'} ${source_uids.length} source${source_uids.length > 1 ? 's' : ''}?`;
      }

      await confirmAndToggleActivation({
        source_uids,
        activate,
        confirmation_msg,
        onSuccess: () => {
          if (gridRef) {
            gridRef.current?.api?.deselectAll();
            setSelectedCloudSourceRows([]);
          }
          onSuccess?.();
        }
      });
    },
    [
      selectedCloudSourceRows,
      getActionForSelectedRows,
      confirmAndToggleActivation,
      setSelectedCloudSourceRows
    ]
  );

  const handleSingleSourceToggle = useCallback(
    async (toggleState: SingleSourceToggleRequest) => {
      await confirmAndToggleActivation(toggleState);
    },
    [confirmAndToggleActivation]
  );

  const handleCancelSelection = useCallback(
    (gridRef: React.RefObject<AgGridReact<CloudSourceTreeNode> | null>) => {
      if (!gridRef.current) {
        return;
      }
      // Clear ALL selections in the grid (not just current page)
      gridRef.current.api?.deselectAll();
      setSelectedCloudSourceRows([]);
    },
    [setSelectedCloudSourceRows]
  );

  return {
    selectedActivationCount,
    actionLabel: getActionLabel(),
    handleActivateDeactivate,
    handleSingleSourceToggle,
    handleCancelSelection,
    ConfirmActivateDeactivateModal
  };
};
